import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AssigneeMemberDisplay } from '@/components/AssigneeMemberDisplay';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganizationUsers } from '@/hooks/useOrganizationUsers';
import { useOrgProgramWorkflows } from '@/hooks/useOrgProgramWorkflows';
import {
  useCreateOrganizationTeam,
  useLinkTeamToProgram,
  useOrganizationTeams,
} from '@/hooks/useTeams';
import { workflowService } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';

import { ALREADY_ON_TEAM_HINT, usersAlreadyOnATeam } from './teamMembership';

interface CreateTeamDialogProps {
  organizationId: string;
  /** When provided, the program is pre-selected and the selector is hidden */
  programId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({
  organizationId,
  programId: fixedProgramId,
  open,
  onOpenChange,
}) => {
  const isProgramScoped = !!fixedProgramId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [managerId, setManagerId] = useState('');
  const [selectedWorkflowTypes, setSelectedWorkflowTypes] = useState<string[]>([]);
  // Org-settings (multi-program) mode state
  const [workflowType, setWorkflowType] = useState('');
  const [selectedProgramIds, setSelectedProgramIds] = useState<string[]>([]);
  const [managerComboboxOpen, setManagerComboboxOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: orgUsersData } = useOrganizationUsers(organizationId, {
    limit: 100,
  });

  const createTeamMutation = useCreateOrganizationTeam();
  const linkTeamMutation = useLinkTeamToProgram();
  const { teams: orgTeams } = useOrganizationTeams(organizationId);

  const orgUsers = orgUsersData?.users || [];
  const selectedManager = orgUsers.find(u => u.user_id === managerId);

  const assignedElsewhere = useMemo(() => usersAlreadyOnATeam(orgTeams), [orgTeams]);

  // ─── Program-scoped mode: workflows for the single (fixed) program ──────────
  const { data: workflowsData } = useQuery({
    queryKey: ['workflows', 'byProgram', organizationId, fixedProgramId],
    queryFn: () =>
      workflowService.getWorkflowsByProgram({
        organization_id: organizationId,
        program_id: fixedProgramId!,
      }),
    enabled: isProgramScoped && open,
  });

  // Build unique workflow type options from program workflows
  const workflowTypeOptionsForProgram = useMemo(() => {
    const types = new Map<string, string>();
    for (const wf of workflowsData?.workflows || []) {
      if (wf.workflow_type && !types.has(wf.workflow_type)) {
        types.set(wf.workflow_type, wf.name || wf.workflow_type);
      }
    }
    return [...types.entries()].map(([value, label]) => ({ value, label }));
  }, [workflowsData]);

  // ─── Org-settings mode: org-wide workflow types and their programs ──────────
  const { workflowTypeOptions, programsByWorkflowType } = useOrgProgramWorkflows(
    open && !isProgramScoped,
  );

  const programOptions = useMemo(
    () =>
      (programsByWorkflowType.get(workflowType) || [])
        .filter(p => p.id)
        .map(p => ({ value: p.id!, label: p.name || p.id! })),
    [programsByWorkflowType, workflowType],
  );

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setManagerId('');
      setSelectedWorkflowTypes([]);
      setWorkflowType('');
      setSelectedProgramIds([]);
      setIsSubmitting(false);
    }
  }, [open]);

  // Org mode: auto-select the workflow type when there is exactly one option.
  useEffect(() => {
    if (open && !isProgramScoped && !workflowType && workflowTypeOptions.length === 1) {
      setWorkflowType(workflowTypeOptions[0].value);
    }
  }, [open, isProgramScoped, workflowType, workflowTypeOptions]);

  // Org mode: reset the program selection whenever the workflow type changes.
  useEffect(() => {
    if (!isProgramScoped) setSelectedProgramIds([]);
  }, [isProgramScoped, workflowType]);

  const canSubmit = name.trim()
    ? isProgramScoped
      ? true
      : !!workflowType && selectedProgramIds.length > 0
    : false;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      if (isProgramScoped) {
        const response = await createTeamMutation.mutateAsync({
          organization_id: organizationId,
          name: name.trim(),
          description: description.trim() || undefined,
          manager_id: managerId || undefined,
          workflow_types: selectedWorkflowTypes.length > 0 ? selectedWorkflowTypes : undefined,
        });

        if (response.team?.id && fixedProgramId) {
          await linkTeamMutation.mutateAsync({
            organizationId,
            programId: fixedProgramId,
            teamId: response.team.id,
          });
        }

        toast.success(`Team "${name.trim()}" created and linked to program`);
      } else {
        const response = await createTeamMutation.mutateAsync({
          organization_id: organizationId,
          name: name.trim(),
          description: description.trim() || undefined,
          manager_id: managerId || undefined,
          workflow_types: [workflowType],
        });

        if (response.team?.id) {
          await Promise.all(
            selectedProgramIds.map(pid =>
              linkTeamMutation.mutateAsync({
                organizationId,
                programId: pid,
                teamId: response.team!.id!,
              }),
            ),
          );
        }

        const count = selectedProgramIds.length;
        toast.success(
          `Team "${name.trim()}" created and linked to ${count} ${
            count === 1 ? 'program' : 'programs'
          }`,
        );
      }

      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create team'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Team</DialogTitle>
          <DialogDescription>
            {isProgramScoped
              ? 'Create a new team and link it to a program.'
              : 'Create a new team and assign it to programs by workflow type.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="team-name">Name</Label>
            <Input
              id="team-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter team name"
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="team-description">Description</Label>
            <Input
              id="team-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description"
              className="mt-2"
            />
          </div>

          <div>
            <Label>Team Lead</Label>
            <Popover open={managerComboboxOpen} onOpenChange={setManagerComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={managerComboboxOpen}
                  className="mt-2 w-full justify-between"
                >
                  {selectedManager
                    ? selectedManager.name || selectedManager.email
                    : 'Select a team lead...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[450px] p-0">
                <Command>
                  <CommandInput placeholder="Search users..." />
                  <CommandEmpty>No users found.</CommandEmpty>
                  <CommandList
                    className="max-h-[200px] overflow-y-auto"
                    onWheel={e => e.stopPropagation()}
                  >
                    <CommandGroup>
                      {orgUsers.map(user => {
                        const isDisabled = assignedElsewhere.has(user.user_id || '');
                        return (
                          <CommandItem
                            key={user.user_id}
                            value={`${user.name} ${user.email}`}
                            disabled={isDisabled}
                            onSelect={() => {
                              if (isDisabled) return;
                              setManagerId(user.user_id || '');
                              setManagerComboboxOpen(false);
                            }}
                          >
                            <AssigneeMemberDisplay
                              name={user.name || undefined}
                              email={user.email || ''}
                              userId={user.user_id}
                            />
                            {isDisabled && (
                              <span className="ml-auto text-xs text-muted-foreground">
                                {ALREADY_ON_TEAM_HINT}
                              </span>
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Program-scoped mode: single fixed program + optional workflow types */}
          {isProgramScoped ? (
            workflowTypeOptionsForProgram.length > 0 && (
              <div>
                <Label>Workflow Types</Label>
                <MultiSelect
                  options={workflowTypeOptionsForProgram}
                  selected={selectedWorkflowTypes}
                  onChange={setSelectedWorkflowTypes}
                  placeholder="Select workflow types (optional)"
                  className="mt-2"
                  searchable
                />
              </div>
            )
          ) : (
            /* Org-settings mode: workflow type → filtered multi-program association */
            <>
              <div>
                <Label>Workflow Type</Label>
                <Select value={workflowType} onValueChange={setWorkflowType}>
                  <SelectTrigger className="mt-2 text-left">
                    <SelectValue placeholder="Select a workflow type" />
                  </SelectTrigger>
                  <SelectContent>
                    {workflowTypeOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Programs</Label>
                <MultiSelect
                  className="mt-2"
                  options={programOptions}
                  selected={selectedProgramIds}
                  onChange={setSelectedProgramIds}
                  placeholder={workflowType ? 'Select programs' : 'Select a workflow type first'}
                  disabled={!workflowType}
                  searchable
                  maxCount={2}
                  countNoun="program"
                />
                {workflowType && programOptions.length === 0 && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    No programs have this workflow type.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
